import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Send, LifeBuoy } from 'lucide-react';
import axios from 'axios';
import API_URL from '../../utils/apiUrl';

const ContactCoach = ({ getAuthHeader, userId }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchMessages();
    const interval = setInterval(fetchMessages, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/operator/support-chat/messages`, { headers: getAuthHeader() });
      setMessages(res.data);
    } catch (e) { console.error(e); }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      await axios.post(`${API_URL}/api/operator/support-chat/send`, {
        content: newMessage.trim()
      }, { headers: { ...getAuthHeader(), 'Content-Type': 'application/json' } });
      setNewMessage('');
      fetchMessages();
    } catch (e) { console.error(e); }
    setSending(false);
  };

  return (
    <Card className="flex flex-col h-full" data-testid="contact-coach-chat">
      <CardHeader className="py-3 px-4 border-b shrink-0">
        <CardTitle className="text-sm flex items-center gap-2">
          <LifeBuoy className="h-4 w-4 text-green-500" /> Contact COACH
        </CardTitle>
      </CardHeader>
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-3">
          {messages.length === 0 && (
            <p className="text-center text-sm text-slate-400 py-4">Need help? Send a message to your coach!</p>
          )}
          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.sender_id === userId ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${msg.sender_id === userId ? 'bg-green-500 text-white' : 'bg-slate-100 text-slate-800'}`}>
                <div className={`text-xs font-medium mb-1 ${msg.sender_id === userId ? 'text-green-100' : 'text-green-600'}`}>
                  {msg.sender_id === userId ? 'You' : 'Coach'}
                </div>
                {msg.content}
                <div className={`text-xs mt-1 ${msg.sender_id === userId ? 'text-green-100' : 'text-slate-400'}`}>
                  {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>
      <form onSubmit={sendMessage} className="p-3 border-t flex gap-2 shrink-0">
        <Input
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Message your coach..."
          data-testid="coach-chat-input"
        />
        <Button type="submit" disabled={sending} size="sm" className="bg-green-500 hover:bg-green-600" data-testid="coach-chat-send">
          <Send className="h-4 w-4" />
        </Button>
      </form>
    </Card>
  );
};

export default ContactCoach;
